import React from 'react';
import { Link } from 'react-router-dom';
import { Typography, Flex, Form, Input, theme, Button } from 'antd';

import { ModalTemplate } from './ModalTemplate';
import { CHURN_REASONS } from '../FreeTrial';
import { useTrial } from '../useTrial';
import useTrackTrialEvent from '../useTrackTrialEvent';

const MEETING_LINK = 'https://meetings-eu1.hubspot.com/anna-makovnikova';

function NotEnoughFeaturesModal() {
  const { token } = theme.useToken();
  const { updateFreeTrialState, freeTrialState } = useTrial();
  const trackTrialEvent = useTrackTrialEvent();

  const open = freeTrialState.user.churnFollowupModal === CHURN_REASONS.not_enough_features;

  const cleanupFreeTrialState = () => {
    updateFreeTrialState({ state: { churnFollowupModal: null }, scope: 'user' });
    updateFreeTrialState({ state: { unconvertedFlowEnded: true }, scope: 'company' });
  };

  const onCancel = () => {
    trackTrialEvent('BannerClosed', { banner_name: 'NotEnoughFeatures' });
    cleanupFreeTrialState();
  };

  const onSubmit = (values: { reason: string }) => {
    trackTrialEvent('FeedbackNotEnoughFeatures', { reason: values.reason });
    cleanupFreeTrialState();
  };

  React.useEffect(() => {
    if (open) {
      trackTrialEvent('BannerShown', { banner_name: 'NotEnoughFeatures' });
    }
  }, [open]);

  return (
    <ModalTemplate
      open={open}
      title="We value your feedback!"
      onCancel={onCancel}
      footer={[
        <Flex justify="space-between" key="footer">
          <Link to="/billing/plans">
            <Button>View plans</Button>
          </Link>

          <Flex gap="small">
            <Button onClick={onCancel} type="text">
              Close
            </Button>
            <Button htmlType="submit" form="notEnoughFeatures" type="primary">
              Submit feedback
            </Button>
          </Flex>
        </Flex>,
      ]}
    >
      <Typography.Paragraph>
        It sounds like the Essential plan didn’t meet your needs. We’re always looking to improve, and your input is
        invaluable.
      </Typography.Paragraph>

      <Flex style={{ marginTop: token.marginLG }} gap="middle" vertical>
        <Typography.Text strong>What features are you missing?</Typography.Text>
        <Form name="notEnoughFeatures" onFinish={onSubmit}>
          <Form.Item
            name="reason"
            rules={[{ required: true, message: 'Please share your feedback' }]}
            style={{ margin: 0 }}
          >
            <Input.TextArea placeholder="Type here" autoSize={{ minRows: 5 }} maxLength={255} showCount />
          </Form.Item>
        </Form>
        <div style={{ marginTop: token.margin }}>
          <Typography.Text>
            Help us make Prerender meet your needs!{' '}
            <Typography.Link
              style={{ color: token.colorPrimary, textDecoration: 'underline' }}
              target="_blank"
              href={MEETING_LINK}
            >
              Book a short call
            </Typography.Link>{' '}
            with our Product Manager and we’ll figure it out.
          </Typography.Text>
        </div>
      </Flex>
    </ModalTemplate>
  );
}

export default NotEnoughFeaturesModal;
